/**
 * 价格图表组件 - 显示K线图和技术指标
 */

import React, { useMemo, useCallback } from 'react';
import { Card, Spin, Alert, Switch, Select, Space, Typography } from 'antd';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { PriceChartProps, PriceData, IndicatorData } from './types';

const { Title, Text } = Typography;
const { Option } = Select;

const PriceChart: React.FC<PriceChartProps> = ({
  data,
  indicators = [],
  config = {},
  loading = false,
  error,
  showVolume = true,
  showIndicators = true,
  indicatorTypes = ['ma5', 'ma20'],
  onDataPointClick,
  onZoomChange,
  onIndicatorTypesChange,
  onShowVolumeChange,
  onShowIndicatorsChange
}) => {
  // 处理K线数据 - 修复数据映射顺序
  // ECharts K线图数据格式：[timestamp, open, high, low, close]
  const candlestickData = useMemo(() => {
    return data.map(item => [
      item.timestamp,
      item.open,
      item.high,
      item.low,
      item.close
    ]);
  }, [data]);

  // 处理成交量数据
  const volumeData = useMemo(() => {
    return data.map(item => [
      item.timestamp,
      item.volume,
      item.close > item.open ? 1 : -1 // 1为阳线，-1为阴线
    ]);
  }, [data]);

  // 处理技术指标数据
  const processIndicatorData = useCallback((indicators: Record<string, IndicatorData>) => {
    const series: any[] = [];
    
    Object.entries(indicators).forEach(([key, indicator]) => {
      if (indicator.type === 'line') {
        series.push({
          name: indicator.name,
          type: 'line',
          data: indicator.data,
          smooth: true,
          lineStyle: {
            color: indicator.color || '#1890ff',
            width: 2
          }
        });
      }
    });
    
    return series;
  }, []);

  const indicatorSeries = useMemo(() => {
    const series: any[] = [];
    const colorMap: Record<string, string> = {
      ma5: '#ff6b6b',
      ma10: '#4ecdc4',
      ma20: '#45b7d1',
      ma50: '#96ceb4',
      macd: '#54a0ff',
      rsi: '#ff9ff3'
    };
    
    if (indicators.length > 0) {
      indicatorTypes.filter(t => t.toLowerCase() !== 'rsi').forEach(type => {
        const indicatorData = indicators.map(item => [
          item.timestamp,
          item[type as keyof IndicatorData] || null
        ]);
        
        if (indicatorData.some(item => item[1] !== null)) {
          series.push({
            name: type.toUpperCase(),
            type: 'line',
            data: indicatorData,
            smooth: true,
            color: colorMap[type.toLowerCase()] || '#1890ff',
            lineStyle: {
              width: 2,
              opacity: 0.8,
              color: colorMap[type.toLowerCase()] || '#1890ff'
            },
            symbol: 'none'
          });
        }
      });
    }
    
    return series;
  }, [indicators, indicatorTypes]);

  // 使用后端指标的RSI与买卖点标记（统一算法，独立轴0-100）
  const rsiFromIndicators = useMemo(() => {
    // 优先使用 RSI (后端标准键名)，其次 rsi
    const getRsi = (it: any) => {
      if (it?.RSI != null) return it.RSI;
      if (it?.rsi != null) return it.rsi;
      if (it?.rsi14 != null) return it.rsi14;
      if (it?.rsi_14 != null) return it.rsi_14;
      return null;
    };
    const rsiSeries = indicators.map(item => [item.timestamp, getRsi(item)]);
    const buys: any[] = [];
    const sells: any[] = [];
    indicators.forEach((item, idx) => {
      const r = getRsi(item);
      if (typeof r === 'number') {
        const ts = item.timestamp;
        const price = data[idx]?.close;
        if (r > 70 && ts && typeof price === 'number') buys.push([ts, price]);
        if (r < 30 && ts && typeof price === 'number') sells.push([ts, price]);
      }
    });
    return { rsi: rsiSeries, buys, sells };
  }, [indicators, data]);

  const rsiUnified = useMemo(() => {
    // 检查是否有有效RSI数据（非空且非NaN）
    const validRsiCount = rsiFromIndicators.rsi.filter((p: any) => p[1] != null && !isNaN(p[1])).length;
    const hasValidRsi = validRsiCount > 0;
    
    if (hasValidRsi) return rsiFromIndicators;
    
    // Fallback: 前端计算（Wilder's Smoothing）
    // 注意：前端计算受限于数据长度，可能与后端全量计算有偏差
    const period = 14;
    const closes = data.map(d => d.close);
    if (closes.length < period + 1) return { rsi: [], buys: [], sells: [] } as any;
    const gains: number[] = []; const losses: number[] = [];
    for (let i = 1; i < closes.length; i++) {
      const diff = closes[i] - closes[i-1];
      gains.push(Math.max(diff, 0));
      losses.push(Math.max(-diff, 0));
    }
    let avgGain = gains.slice(0, period).reduce((a,b)=>a+b,0) / period;
    let avgLoss = losses.slice(0, period).reduce((a,b)=>a+b,0) / period;
    const rsiVals: number[] = [];
    rsiVals.push(100 - (100 / (1 + (avgGain / (avgLoss === 0 ? 1e-12 : avgLoss)))));
    for (let i = period; i < gains.length; i++) {
      avgGain = (avgGain * (period - 1) + gains[i]) / period;
      avgLoss = (avgLoss * (period - 1) + losses[i]) / period;
      const rs = avgGain / (avgLoss === 0 ? 1e-12 : avgLoss);
      const rsi = 100 - (100 / (1 + rs));
      rsiVals.push(rsi);
    }
    const rsiSeries = data.slice(1 + (period - 1)).map((d, i) => [d.timestamp, rsiVals[i] || null]);
    const buys: any[] = []; const sells: any[] = [];
    rsiSeries.forEach((p, idx) => {
      const val = p[1];
      const priceIdx = idx + 1 + (period - 1);
      const ts = data[priceIdx]?.timestamp; const price = data[priceIdx]?.close;
      if (typeof val === 'number' && ts && typeof price === 'number') {
        if (val > 70) buys.push([ts, price]);
        if (val < 30) sells.push([ts, price]);
      }
    });
    return { rsi: rsiSeries, buys, sells };
  }, [rsiFromIndicators, data]);

  const rsiMap = useMemo(() => {
    const map: Record<string, number> = {};
    (rsiUnified.rsi || []).forEach((p: any) => {
      const key = String(p[0]);
      const val = p[1];
      if (typeof val === 'number') map[key] = val as number;
    });
    return map;
  }, [rsiUnified]);

  // 图表配置
  const chartOption = useMemo(() => {
    const option: any = {
      backgroundColor: config.theme?.backgroundColor || 'transparent',
      animation: config.animation !== false,
      grid: [
        {
          left: '10%',
          right: '8%',
          top: '10%',
          height: showVolume ? '50%' : '70%'
        },
        ...(showVolume ? [{
          left: '10%',
          right: '8%',
          top: '70%',
          height: '20%'
        }] : [])
      ],
      xAxis: [
        {
          type: 'category',
          data: data.map(item => item.timestamp),
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false },
          splitLine: { show: config.showGrid !== false },
          min: 'dataMin',
          max: 'dataMax'
        },
        ...(showVolume ? [{
          type: 'category',
          gridIndex: 1,
          data: data.map(item => item.timestamp),
          scale: true,
          boundaryGap: false,
          axisLine: { onZero: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          min: 'dataMin',
          max: 'dataMax'
        }] : [])
      ],
      // 计算RSI轴索引：价格轴=0，成交量轴=1（可选），RSI轴=2或1
      yAxis: [
        {
          scale: true,
          splitArea: { show: true },
          splitLine: { show: config.showGrid !== false },
          axisLabel: {
            formatter: (val: number) => `${Number(val).toFixed(2)}元`
          }
        },
        ...(showVolume ? [{
          scale: true,
          gridIndex: 1,
          splitNumber: 2,
          axisLabel: {
            show: true,
            formatter: (val: number) => {
              if (val >= 1e8) return `${(val / 1e8).toFixed(1)}亿`;
              if (val >= 1e4) return `${(val / 1e4).toFixed(1)}万`;
              return Number(val).toFixed(0);
            }
          },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false }
        }] : []),
        // RSI独立轴（与价格同grid，右侧显示0-100）
        ...(showIndicators && indicatorTypes.includes('rsi') ? [{
          scale: false,
          min: 0,
          max: 100,
          position: 'right',
          axisLine: { show: true },
          axisTick: { show: true },
          splitLine: { show: false },
          axisLabel: { formatter: (val: number) => `${Number(val).toFixed(0)}` }
        }] : [])
      ],
      dataZoom: config.showZoom !== false ? [
        {
          type: 'inside',
          xAxisIndex: showVolume ? [0, 1] : [0],
          start: 80,
          end: 100
        },
        {
          show: true,
          xAxisIndex: showVolume ? [0, 1] : [0],
          type: 'slider',
          top: '90%',
          start: 80,
          end: 100
        }
      ] : undefined,
      series: [
        // K线图
        {
          name: '价格',
          type: 'candlestick',
          // ECharts K线数据格式：[open, close, low, high]
          data: candlestickData.map(item => [item[1], item[4], item[3], item[2]]),
          itemStyle: {
            color: config.theme?.candlestick?.upColor || '#26a69a',
            color0: config.theme?.candlestick?.downColor || '#ef5350',
            borderColor: config.theme?.candlestick?.borderUpColor || '#26a69a',
            borderColor0: config.theme?.candlestick?.borderDownColor || '#ef5350'
          },
          markPoint: {
            label: {
              formatter: function(param: any) {
                return param != null ? Math.round(param.value) + '' : '';
              }
            },
            data: [
              {
                name: 'Mark',
                coord: ['2013/5/31', 2300],
                value: 2300,
                itemStyle: {
                  color: 'rgb(41,60,85)'
                }
              },
              {
                name: 'highest value',
                type: 'max',
                valueDim: 'highest'
              },
              {
                name: 'lowest value',
                type: 'min',
                valueDim: 'lowest'
              }
            ],
            tooltip: {
              formatter: function(param: any) {
                return param.name + '<br>' + (param.data.coord || '');
              }
            }
          }
        },
        // Backtrader风格：RSI与买卖点标记（在主图上叠加）
        ...(indicatorTypes.includes('rsi') ? [
          {
            name: 'RSI',
            type: 'line',
            data: rsiUnified.rsi,
            smooth: true,
            yAxisIndex: (showIndicators && indicatorTypes.includes('rsi')) ? (showVolume ? 2 : 1) : 0,
            lineStyle: { width: 1, color: '#8e44ad', opacity: 0.6 },
            symbol: 'none'
          },
          {
            name: 'BUY',
            type: 'scatter',
            data: rsiUnified.buys,
            symbol: 'triangle',
            symbolSize: 9,
            itemStyle: { color: '#34a853' },
            tooltip: { formatter: (p: any) => `BUY @ ${Number(p.data[1]).toFixed(2)}` }
          },
          {
            name: 'SELL',
            type: 'scatter',
            data: rsiUnified.sells,
            symbol: 'triangle',
            symbolRotate: 180,
            symbolSize: 9,
            itemStyle: { color: '#d93025' },
            tooltip: { formatter: (p: any) => `SELL @ ${Number(p.data[1]).toFixed(2)}` }
          }
        ] : []),
        // 技术指标
        ...(showIndicators ? indicatorSeries : []),
        // 成交量
        ...(showVolume ? [{
          name: '成交量',
          type: 'bar',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: volumeData.map(item => [item[0], item[1]]),
          itemStyle: {
            color: function(params: any) {
                const volumeItem = volumeData[params.dataIndex];
                return volumeItem && Number(volumeItem[2]) > 0 ? '#26a69a' : '#ef5350';
              }
          }
        }] : [])
      ],
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(245, 245, 245, 0.8)',
        borderWidth: 1,
        borderColor: '#ccc',
        padding: 10,
        textStyle: {
          color: '#000'
        },
        formatter: (params: any) => {
          let result = params[0].name + '<br/>';
          
          params.forEach((param: any) => {
            if (param.seriesName === '价格') {
              const data = param.data;
              result += `开盘: ${data[0]}<br/>`;
              result += `收盘: ${data[1]}<br/>`;
              result += `最低: ${data[2]}<br/>`;
              result += `最高: ${data[3]}<br/>`;
            } else if (param.seriesName === '成交量') {
              result += `成交量: ${param.data[1]}<br/>`;
            } else if (param.seriesName === 'RSI') {
              result += `RSI: ${Number(param.data[1]).toFixed(2)}<br/>`;
            } else if (param.seriesName === 'BUY' || param.seriesName === 'SELL') {
              const ts = String(param.data[0]);
              const price = Number(param.data[1]);
              const rsi = rsiMap[ts];
              result += `${param.seriesName} 信号 @ ${price.toFixed(2)}${typeof rsi === 'number' ? `（RSI: ${rsi.toFixed(2)}）` : ''}<br/>`;
            } else {
              result += `${param.seriesName}: ${param.data[1]}<br/>`;
            }
          });
          
          return result;
        }
      },
      legend: config.showLegend !== false ? {
        data: ['价格', ...(indicatorTypes.filter(t=>t.toLowerCase()!=='rsi').map(type => type.toUpperCase())), ...(indicatorTypes.includes('rsi') ? ['RSI','BUY','SELL'] : []), ...(showVolume ? ['成交量'] : [])]
      } : undefined
    };

    return option;
  }, [data, indicators, indicatorSeries, candlestickData, volumeData, config, showVolume, indicatorTypes]);

  // 处理图表事件
  const handleChartEvents = useCallback(() => {
    return {
      click: (params: any) => {
        if (onDataPointClick && params.data) {
          const dataIndex = params.dataIndex;
          if (dataIndex >= 0 && dataIndex < data.length) {
            onDataPointClick(data[dataIndex]);
          }
        }
      },
      datazoom: (params: any) => {
        if (onZoomChange) {
          const startIndex = Math.floor(params.start * data.length / 100);
          const endIndex = Math.floor(params.end * data.length / 100);
          if (startIndex >= 0 && endIndex < data.length) {
            onZoomChange(data[startIndex].timestamp, data[endIndex].timestamp);
          }
        }
      }
    };
  }, [data, onDataPointClick, onZoomChange]);

  if (error) {
    return (
      <Card>
        <Alert
          message="图表加载失败"
          description={error}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card id="price-chart-capture"
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>价格走势图</Title>
          {showIndicators && (
            <Select
              mode="multiple"
              placeholder="选择技术指标"
              value={indicatorTypes}
              style={{ minWidth: 200 }}
              size="small"
              onChange={onIndicatorTypesChange}
            >
              <Option value="ma5">MA5</Option>
              <Option value="ma10">MA10</Option>
              <Option value="ma20">MA20</Option>
              <Option value="ma50">MA50</Option>
              <Option value="rsi">RSI</Option>
              <Option value="macd">MACD</Option>
            </Select>
          )}
        </Space>
      }
      extra={
        <Space>
          <Text>成交量</Text>
          <Switch
            checked={showVolume}
            size="small"
            onChange={onShowVolumeChange}
          />
          <Text>技术指标</Text>
          <Switch
            checked={showIndicators}
            size="small"
            onChange={onShowIndicatorsChange}
          />
        </Space>
      }
      loading={loading}
    >
      <Spin spinning={loading}>
        <ReactECharts
          option={chartOption}
          style={{
            height: config.height || 600,
            width: config.width || '100%'
          }}
          onEvents={handleChartEvents()}
          opts={{
          renderer: 'canvas'
        }}
        />
      </Spin>
    </Card>
  );
};

export default PriceChart;
